import { useEffect, useRef, useState } from "react";
import Icon from "../components/Icon";
import { Spinner } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

/* =========================================================================
   QUÉ LOCAL SE ESTÁ ADMINISTRANDO

   Un tendero puede tener más de un negocio. La app móvil se queda con el
   primero de la lista y nunca deja llegar al segundo: quien tiene dos locales
   administra uno desde el teléfono y el otro no lo ve nunca. Acá se cambia.

   VA EN LA BARRA SUPERIOR y no en el menú lateral porque el menú se contrae y
   en pantalla angosta se esconde tras un botón, y este control tiene que estar
   visible siempre: es lo que responde "¿de qué tienda son estos pedidos?".
   Un panel donde esa pregunta se contesta mal no es un panel con un error de
   navegación, es uno que hace despachar el pedido equivocado.

   Con un solo local no se pinta: un desplegable de una opción ocupa sitio y no
   decide nada. De eso se encarga quien lo monta.
   ======================================================================== */

export default function SelectorDeNegocio() {
  const { negocios, negocioId, area, cambiarNegocio } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const [cambiando, setCambiando] = useState(false);
  const caja = useRef(null);
  const toast = useToast();

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => {
      if (!caja.current?.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  // Escape cierra: es un menú superpuesto y quien navega con teclado espera
  // poder salir sin tener que recorrerlo entero.
  useEffect(() => {
    if (!abierto) return;
    const tecla = (e) => e.key === "Escape" && setAbierto(false);
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [abierto]);

  if (!negocios || negocios.length < 2) return null;

  const elegir = async (id) => {
    if (Number(id) === Number(negocioId)) {
      setAbierto(false);
      return;
    }

    setCambiando(true);
    const r = await cambiarNegocio(id);
    setCambiando(false);
    setAbierto(false);

    if (!r.ok) toast.error(r.message ?? "No se pudo cambiar de negocio.");
  };

  const actual = negocios.find((n) => Number(n.busines_id) === Number(negocioId));

  return (
    <div className="selector-negocio" ref={caja}>
      <button
        className="chip-negocio"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        disabled={cambiando}
        title="Cambiar de negocio"
      >
        <span className="chip-negocio-icono" aria-hidden="true">
          {cambiando ? <Spinner size={13} /> : <Icon name="store" size={15} />}
        </span>
        <span className="chip-negocio-texto">
          <span className="chip-negocio-rotulo">Administrando</span>
          <span className="chip-negocio-nombre truncate">
            {actual?.name ?? area?.name ?? "Elegir negocio"}
          </span>
        </span>
        <Icon name="chevronDown" size={13} className="faint" />
      </button>

      {abierto && (
        <div className="menu menu-negocios" role="listbox">
          <div className="menu-head menu-head-simple">
            Tus negocios
            <span className="faint"> · {negocios.length}</span>
          </div>

          {negocios.map((n) => {
            const activo = Number(n.busines_id) === Number(negocioId);

            return (
              <button
                key={n.busines_id}
                role="option"
                aria-selected={activo}
                className={`menu-item item-negocio${activo ? " activo" : ""}`}
                onClick={() => elegir(n.busines_id)}
              >
                <span className="item-negocio-nombre truncate">{n.name}</span>

                {/* Un local dado de baja por la plataforma sigue en la lista:
                    sus pedidos y su historial existen. Se dice, para que no
                    parezca que dejó de llegarle gente por casualidad. */}
                {!n.state && <span className="pill-inactivo">Inactivo</span>}

                {activo && <Icon name="check" size={15} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
